import type { MenuItem } from '@/consts/navigation';
import { useMeasuredMaxHeight } from '@/hooks/useMeasuredMaxHeight';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';

import { useNavigationContext } from '../context/NavigationContext';
import { NavMenuItemsList } from './NavMenuItemsList';

interface NavSubMenuListProps {
  item: MenuItem;
  index: number;
  className?: string;
}

export const NavSubMenuList = ({ item, index, className }: NavSubMenuListProps) => {
  const { t } = useTranslation();
  const { expandedSections, isNavCollapsed } = useNavigationContext();

  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedSections.has(item.text);
  const isUtilitySection = item.text === 'navigation.recent.title' || item.text === 'navigation.favorites.title';

  const { contentRef, contentHeight } = useMeasuredMaxHeight(isExpanded, [hasChildren, item.children]);

  if (!hasChildren || isNavCollapsed) {
    return null;
  }

  return (
    <div
      id={`section-${index.toString()}`}
      data-testid={`submenu-section-${t(item.text).toLowerCase().replace(/\s+/g, '-')}`}
      className={cn(
        'overflow-hidden transition-all duration-200 ease-in-out',
        isExpanded ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={{ maxHeight: isExpanded ? contentHeight : 0 }}
      aria-hidden={!isExpanded}
    >
      <div ref={contentRef}>
        <div className="border-border-500/60 mt-0.5 ml-[18px] space-y-0.5 border-l pl-2.5">
          <NavMenuItemsList items={item.children ?? []} variant="expanded" isUtilitySection={isUtilitySection} />
        </div>
      </div>
    </div>
  );
};
